import { LIVE_COORD_MAX, LIVE_FRAME_MAX_BYTES, LIVE_TOKEN_MAX, hasExactly, isEditorId, isEditorName, isMoment, type Fields } from "./protocol-shared.ts";
import { isSkippableName, publishOf, type PublishEnded, type PublishRunning, type SkippedMessage } from "./protocol-publish.ts";
import { LIVE_OP_FRAME_MAX, LIVE_SERVER_FRAME_MAX, streamClientOf, streamServerOf, type StreamClientMessage, type StreamServerMessage } from "./protocol-stream.ts";

/**
 * The live channel's messages (ADR-086 decisions 1, 2, 21 and 28): JSON text frames between a tab of a hosted
 * Daedalus and its build server, one socket per tab and world. A tab sends its credential (`hello`, then
 * `token` on each renewal), its cursor and its view; the server sends the room's editors as they come, move and
 * go, and each save of the world. Beside these frames of presence both ends speak the stream's messages
 * (`protocol-stream.ts`) once the server runs the shared operation stream. Both ends read every frame with the
 * parsers here, which accept a message only when it has exactly its fields, each of its type and within its
 * bounds, so neither end acts on anything else. A frame that carries an operation is read up to
 * `LIVE_OP_FRAME_MAX` code units; every other frame of a tab up to `LIVE_FRAME_MAX_BYTES` and of the server up
 * to `LIVE_SERVER_FRAME_MAX`. The credential is opaque here: the host decides what a token is.
 */

export { LIVE_COORD_MAX, LIVE_FRAME_MAX_BYTES, LIVE_NAME_MAX, LIVE_TOKEN_MAX } from "./protocol-shared.ts";
export { PUBLISH_ELAPSED_MAX, PUBLISH_KIND_MAX, PUBLISH_LEFT_MAX, PUBLISH_PHASES, PUBLISH_REASONS, PUBLISH_TOTAL_MAX } from "./protocol-publish.ts";
export type { PublishEnded, PublishPhase, PublishReason, PublishRunning, SkippedMessage } from "./protocol-publish.ts";

const KNOWN_TYPES: ReadonlySet<string> = new Set(["hello", "token", "cursor", "view", "welcome", "joined", "left", "saved", "publish", "resume", "op", "claim", "release", "save", "stream", "ack", "nack", "resync", "claims", "version", "unchanged", "unsaved", "checkpoint", "library", "frozen", "replacing"]);

/**
 * A map's id as the live protocol and content that names a map accept it.
 */
export const MAP_ID_PATTERN = /^[A-Za-z0-9._-]{1,128}$/;

const MAP_ID = MAP_ID_PATTERN;
const TOKEN = /^[\x21-\x7e]+$/;

/**
 * A cursor: the map it is on and its point in world pixels.
 */
export interface LiveCursor {
  readonly map: string;
  readonly x: number;
  readonly y: number;
}

/**
 * A view: the map in view and the camera's rectangle in world pixels.
 */
export interface LiveView {
  readonly map: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Another editor of the world as the room knows it: an opaque id the server gave its socket, the name the host
 * shows (never one the tab chose), and what it last sent.
 */
export interface LiveEditor {
  readonly id: string;
  readonly name: string;
  readonly cursor: LiveCursor | null;
  readonly view: LiveView | null;
}

/**
 * What a tab sends: presence's messages and the stream's.
 */
export type LiveClientMessage = { readonly t: "hello"; readonly token: string } | { readonly t: "token"; readonly token: string } | ({ readonly t: "cursor" } & LiveCursor) | ({ readonly t: "view" } & LiveView) | StreamClientMessage;

/**
 * What the server sends: presence's messages and the stream's.
 */
export type LiveServerMessage =
  | { readonly t: "welcome"; readonly you: string; readonly editors: readonly LiveEditor[] }
  | { readonly t: "joined"; readonly editor: LiveEditor }
  | { readonly t: "left"; readonly id: string }
  | ({ readonly t: "cursor"; readonly id: string } & LiveCursor)
  | ({ readonly t: "view"; readonly id: string } & LiveView)
  | { readonly t: "saved"; readonly sequence: number; readonly at: string; readonly name: string | null }
  | { readonly t: "replacing"; readonly by: string }
  | PublishRunning
  | PublishEnded
  | SkippedMessage
  | StreamServerMessage;

/**
 * Parses a frame as a JSON object.
 *
 * @param text The frame.
 * @param max The longest frame read.
 * @returns The object, or null when the frame is too long, not JSON or not an object.
 */
function objectOf(text: string, max: number): Fields | null {
  if (text.length > max) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Fields) : null;
  } catch {
    return null;
  }
}

/**
 * Whether a value is a coordinate within bounds.
 *
 * @param value The value.
 * @param minimum The least allowed.
 * @returns True for a finite number from `minimum` to `LIVE_COORD_MAX`.
 */
function isCoord(value: unknown, minimum = -LIVE_COORD_MAX): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= minimum && value <= LIVE_COORD_MAX;
}

/**
 * Whether a value matches a pattern as a string.
 *
 * @param value The value.
 * @param pattern The pattern.
 * @returns True for a matching string.
 */
function isText(value: unknown, pattern: RegExp): value is string {
  return typeof value === "string" && pattern.test(value);
}

/**
 * Reads a cursor's fields.
 *
 * @param fields The fields.
 * @returns The cursor, or null when one is missing or out of bounds.
 */
function cursorOf(fields: Fields): LiveCursor | null {
  return isText(fields["map"], MAP_ID) && isCoord(fields["x"]) && isCoord(fields["y"]) ? { map: fields["map"], x: fields["x"], y: fields["y"] } : null;
}

/**
 * Reads a view's fields.
 *
 * @param fields The fields.
 * @returns The view, or null when one is missing or out of bounds.
 */
function viewOf(fields: Fields): LiveView | null {
  const cursor = cursorOf(fields);
  return cursor !== null && isCoord(fields["w"], 0) && isCoord(fields["h"], 0) ? { ...cursor, w: fields["w"], h: fields["h"] } : null;
}

/**
 * Reads a tab's frame.
 *
 * @param text The frame.
 * @returns The message, or null when the frame is not exactly one: a frame that carries an operation is read
 *   up to `LIVE_OP_FRAME_MAX` code units and any other up to `LIVE_FRAME_MAX_BYTES`.
 */
export function parseClientMessage(text: string): LiveClientMessage | null {
  const fields = objectOf(text, LIVE_OP_FRAME_MAX);
  if (fields === null || (fields["t"] !== "op" && text.length > LIVE_FRAME_MAX_BYTES)) {
    return null;
  }
  switch (fields["t"]) {
    case "hello":
    case "token": {
      const token = fields["token"];
      return hasExactly(fields, ["t", "token"]) && isText(token, TOKEN) && token.length <= LIVE_TOKEN_MAX ? { t: fields["t"], token } : null;
    }
    case "cursor": {
      const cursor = hasExactly(fields, ["t", "map", "x", "y"]) ? cursorOf(fields) : null;
      return cursor === null ? null : { t: "cursor", ...cursor };
    }
    case "view": {
      const view = hasExactly(fields, ["t", "map", "x", "y", "w", "h"]) ? viewOf(fields) : null;
      return view === null ? null : { t: "view", ...view };
    }
    default:
      return streamClientOf(fields);
  }
}

/**
 * Reads an editor.
 *
 * @param value The value.
 * @returns The editor, or null when it is not one.
 */
function editorOf(value: unknown): LiveEditor | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const fields = value as Fields;
  if (!hasExactly(fields, ["id", "name", "cursor", "view"]) || !isEditorId(fields["id"]) || !isEditorName(fields["name"])) {
    return null;
  }
  const cursorField = fields["cursor"];
  const viewField = fields["view"];
  const cursor = cursorField === null ? null : typeof cursorField === "object" && hasExactly(cursorField as Fields, ["map", "x", "y"]) ? cursorOf(cursorField as Fields) : undefined;
  const view = viewField === null ? null : typeof viewField === "object" && hasExactly(viewField as Fields, ["map", "x", "y", "w", "h"]) ? viewOf(viewField as Fields) : undefined;
  if (cursor === undefined || view === undefined || (cursorField !== null && cursor === null) || (viewField !== null && view === null)) {
    return null;
  }
  return { id: fields["id"], name: fields["name"], cursor, view };
}

/**
 * Reads the server's frame.
 *
 * @param text The frame.
 * @returns The message; a skipped message for a well formed frame whose `t` is 1 to 32 lowercase letters and a type
 *   it does not know, or a `publish` message of a `kind` it does not know; null when the frame is not exactly
 *   one: a known type of a wrong shape is null, and a frame that carries an operation is read up to
 *   `LIVE_OP_FRAME_MAX` code units and any other up to `LIVE_SERVER_FRAME_MAX`.
 */
export function parseServerMessage(text: string): LiveServerMessage | null {
  const fields = objectOf(text, LIVE_OP_FRAME_MAX);
  if (fields === null || (fields["t"] !== "op" && text.length > LIVE_SERVER_FRAME_MAX)) {
    return null;
  }
  switch (fields["t"]) {
    case "welcome": {
      const editors = fields["editors"];
      if (!hasExactly(fields, ["t", "you", "editors"]) || !isEditorId(fields["you"]) || !Array.isArray(editors)) {
        return null;
      }
      const read = editors.map(editorOf);
      return read.every((editor) => editor !== null) ? { t: "welcome", you: fields["you"], editors: read as LiveEditor[] } : null;
    }
    case "joined": {
      const editor = hasExactly(fields, ["t", "editor"]) ? editorOf(fields["editor"]) : null;
      return editor === null ? null : { t: "joined", editor };
    }
    case "left":
      return hasExactly(fields, ["t", "id"]) && isEditorId(fields["id"]) ? { t: "left", id: fields["id"] } : null;
    case "cursor": {
      const cursor = hasExactly(fields, ["t", "id", "map", "x", "y"]) && isEditorId(fields["id"]) ? cursorOf(fields) : null;
      return cursor === null ? null : { t: "cursor", id: fields["id"] as string, ...cursor };
    }
    case "view": {
      const view = hasExactly(fields, ["t", "id", "map", "x", "y", "w", "h"]) && isEditorId(fields["id"]) ? viewOf(fields) : null;
      return view === null ? null : { t: "view", id: fields["id"] as string, ...view };
    }
    case "saved": {
      const sequence = fields["sequence"];
      const at = fields["at"];
      const name = fields["name"];
      const valid = hasExactly(fields, ["t", "sequence", "at", "name"]) && typeof sequence === "number" && Number.isSafeInteger(sequence) && sequence > 0 && isMoment(at) && (name === null || isEditorName(name));
      return valid ? { t: "saved", sequence: sequence as number, at: at as string, name: name as string | null } : null;
    }
    case "publish":
      return publishOf(fields);
    case "replacing":
      return hasExactly(fields, ["t", "by"]) && isEditorName(fields["by"]) ? { t: "replacing", by: fields["by"] } : null;
    default: {
      const type = fields["t"];
      return streamServerOf(fields) ?? (isSkippableName(type) && !KNOWN_TYPES.has(type) ? { t: "skipped", of: type } : null);
    }
  }
}

/**
 * Writes a message as its frame.
 *
 * @param message The message.
 * @returns The frame.
 */
export function encodeLive(message: LiveClientMessage | LiveServerMessage): string {
  return JSON.stringify(message);
}
